import {Component, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatMenuModule} from '@angular/material/menu';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TablerIconsModule} from 'angular-tabler-icons';
import {Roles} from '@core/enums/Roles';
import {User} from '@interfaces/user';
import {Account, AdminApiService, ClientCompany, apiErrorMessage} from '@services/api/admin/admin-api.service';
import {UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import {ModalsConfirmComponent} from '@components/modals/modals-confirm/modals-confirm.component';
import {AccountDialogComponent, AccountDialogData} from '../dialogs/account-dialog.component';
import {InviteLinkDialogComponent} from '../dialogs/invite-link-dialog.component';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [FormsModule, MatMenuModule, MatSelectModule, MatFormFieldModule, MatProgressSpinnerModule, TranslateModule, TablerIconsModule],
  templateUrl: './admin-users.component.html',
  styleUrl: '../admin-page.scss',
})
export class AdminUsersComponent implements OnInit {
  private api = inject(AdminApiService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private userStorage = inject(UserStorageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  me: User | null = null;
  accounts: Account[] = [];
  companies: ClientCompany[] = [];
  /** 0 = tutte le aziende (mat-select tratta null come "nessuna scelta" e non ne mostra l'etichetta) */
  companyFilter = 0;
  search = '';
  loading = true;
  error = '';

  async ngOnInit(): Promise<void> {
    this.me = await this.userStorage.getUserStorage();
    const fromQuery = Number(this.route.snapshot.queryParamMap.get('company'));
    this.companyFilter = fromQuery || 0;
    if (this.isPlatformAdmin) {
      this.api.listCompanies().subscribe({next: (companies) => (this.companies = companies)});
    }
    this.load();
  }

  get isPlatformAdmin(): boolean {
    return this.me?.roles?.id === Roles.SUPER_ADMIN;
  }

  get visibleAccounts(): Account[] {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return this.accounts;
    }
    return this.accounts.filter((a) =>
      `${a.name} ${a.lastname ?? ''} ${a.email} ${a.company_name}`.toLowerCase().includes(term));
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api.listAccounts(this.companyFilter || undefined).subscribe({
      next: (accounts) => {
        this.accounts = accounts;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = apiErrorMessage(err, this.translate.instant('admin.error_load'));
      },
    });
  }

  onCompanyFilter(): void {
    this.router.navigate([], {queryParams: {company: this.companyFilter || null}, replaceUrl: true});
    this.load();
  }

  isSelf(account: Account): boolean {
    return account.id === this.me?.id;
  }

  openCreate(): void {
    this.openAccountDialog({
      companies: this.companies,
      isPlatformAdmin: this.isPlatformAdmin,
      presetCompany: this.companyFilter || undefined,
    });
  }

  openEdit(account: Account): void {
    this.openAccountDialog({account, companies: this.companies, isPlatformAdmin: this.isPlatformAdmin, selfId: this.me?.id});
  }

  resendInvite(account: Account): void {
    this.api.accountAction(account.id, 'invite').subscribe({
      next: (updated) => this.showInvite(updated),
      error: (err) => this.toast(apiErrorMessage(err, this.translate.instant('admin.error_generic'))),
    });
  }

  setActive(account: Account, active: boolean): void {
    const run = () => this.api.accountAction(account.id, active ? 'activate' : 'deactivate').subscribe({
      next: () => {
        this.toast(this.translate.instant(active ? 'admin.user_activated' : 'admin.user_deactivated'));
        this.load();
      },
      error: (err) => this.toast(apiErrorMessage(err, this.translate.instant('admin.error_generic'))),
    });
    if (active) {
      run();
      return;
    }
    this.dialog.open(ModalsConfirmComponent, {
      panelClass: 'dm-admin-dialog',
      data: {
        title: 'admin.deactivate_title',
        message: this.translate.instant('admin.deactivate_message', {name: this.displayName(account)}),
        confirmText: 'admin.deactivate',
        cancelText: 'admin.cancel',
      },
    }).afterClosed().subscribe((confirmed) => confirmed && run());
  }

  displayName(account: Account): string {
    return `${account.name} ${account.lastname ?? ''}`.trim() || account.email;
  }

  private openAccountDialog(data: AccountDialogData): void {
    this.dialog.open(AccountDialogComponent, {data, width: '560px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'})
      .afterClosed().subscribe((account?: Account) => {
        if (!account) {
          return;
        }
        if (account.invite_link) {
          this.showInvite(account);
        } else {
          this.toast(this.translate.instant('admin.user_saved'));
        }
        this.load();
      });
  }

  private showInvite(account: Account): void {
    this.dialog.open(InviteLinkDialogComponent, {data: account, width: '560px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'});
  }

  private toast(message: string): void {
    this.snackBar.open(message, this.translate.instant('admin.close'), {duration: 4000});
  }
}
